import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import {
  CustomTooltip,
  MuiBox,
  MuiTypography,
  SearchInput,
  SubmitLoaderButton,
} from "components/atoms";
import { useDispatch, useSelector } from "react-redux";
import { closeCenterModal } from "store/slices/CenterModal.slice";
import { useTranslation } from "react-i18next";

// SVGs
import DropdownArrowIcon from "assets/images/svg-icons/chevron-right.svg";
import ArrowIcon from "assets/images/svg-icons/arrow-curve-left-right.svg";
import FolderIcon from "assets/images/svg-icons/movefoldericon.svg";
import HelpIcon from "assets/images/svg-icons/help-circle-contained-Black.svg";
import closeIcon from "assets/images/svg-icons/closeIcon.svg";
import ArrowRight from "assets/images/svg-icons/chevron-right.svg";

import {
  GetChildFolderAction,
  GetMoveFoldersAction,
  MovesFileFolderAction,
} from "store/middlewares/folders";
import { setNullChildFolders } from "store/slices/folders";
import NoDataFound from "components/atoms/NoDataFound";
import MoveFileFolderSkeleton from "../SkeletonLoaders/MoveFileFolderSkeleton";
import { openToast } from "store/slices/toast.slice";
import { getApiMessage } from "utils/apiErrorCode";

const MoveFileFolderModal = ({
  type = "folder",
  folderIds = [],
  fileIds = [],
  parentIds = [],
  onClose,
  clearSelection,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [folderHierarchy, setFolderHierarchy] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState();
  const [selectedFolderId, setSelectedFolderId] = useState(undefined);

  const { moveFolders, childFolders, moveFoldersLoading, movingFolderLoading } =
    useSelector((state) => state.folders);

  const { moveFileLoading } = useSelector((state) => state.files);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    setFilesPage(1);

    dispatch(
      GetMoveFoldersAction({
        type: "folder",
        page: 1,
        limit: 10,
        search: debouncedSearch,
        ...(folderHierarchy.length > 0 && {
          folderId: folderHierarchy[folderHierarchy.length - 1]?.id,
        }),
      }),
    );

    dispatch(setNullChildFolders());
  }, [dispatch, debouncedSearch]);

  const [filesPage, setFilesPage] = useState(1);
  const isLoadingRef = useRef(false);
  const isCurrentFolder = expandedFolders?.id === selectedFolderId;

  const isSourceFolder = [...folderIds, ...parentIds].includes(
    selectedFolderId,
  );
  const isAlreadyInRoot = selectedFolderId === null && parentIds.length === 0;

  const isMoveDisabled =
    selectedFolderId === undefined ||
    isSourceFolder ||
    isCurrentFolder ||
    isAlreadyInRoot;

  const handleFilesLoadMore = useCallback(() => {
    if (!moveFoldersLoading && filesPage < moveFolders?.totalPages) {
      const nextPage = filesPage + 1;
      setFilesPage(nextPage);
      dispatch(
        GetMoveFoldersAction({
          page: nextPage,
          limit: 10,
          search: debouncedSearch,
        }),
      );
    }
  }, [
    moveFoldersLoading,
    filesPage,
    moveFolders?.totalPages,
    dispatch,
    debouncedSearch,
  ]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (
        !isLoadingRef.current &&
        !moveFoldersLoading &&
        filesPage < moveFolders?.totalPages
      ) {
        isLoadingRef.current = true;
        handleFilesLoadMore();
        setTimeout(() => {
          isLoadingRef.current = false;
        }, 500);
      }
    }
  };

  const handleSelectFolder = (folder) => {
    setSelectedFolderId(folder?.id ?? null);
  };

  const handleMoveFolder = () => {
    dispatch(
      MovesFileFolderAction({
        folderIds,
        fileIds,
        parentId: selectedFolderId,
        onSuccess: () => {
          onClose?.();
          clearSelection?.();
        },
        onError: (res) => {
          dispatch(closeCenterModal());
          clearSelection?.();
          if (res?.statusCode === 5) {
            dispatch(
              openToast({
                type: "error",
                message: t("modal.moveTo.folderNotFound"),
              }),
            );
          } else {
            dispatch(
              openToast({
                type: "error",
                message: getApiMessage(res?.statusCode),
              }),
            );
          }
        },
      }),
    );
  };

  const toggleFolder = (folder) => {
    setExpandedFolders(folder);

    setFolderHierarchy((prev) => {
      return [...prev, folder];
    });
    setSelectedFolderId(undefined);
    dispatch(GetChildFolderAction({ id: folder.id }));
  };

  const renderFolders = (folders) => {
    return folders?.map((item) => {
      const hasChildren =
        item?.folder_details?.total_folders > 0 || item?._count?.children > 0;
      const isDisabledToNavigate = folderIds.includes(item?.id);

      return (
        <MuiBox key={item.id}>
          <MuiBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "8px",
              pl: "8px",
              cursor: "pointer",
              bgcolor:
                selectedFolderId === item.id
                  ? "background.blue200"
                  : "transparent",
              borderRadius: "6px",
            }}
            onClick={() => handleSelectFolder(item)}
            onDoubleClick={() => {
              // Only open folders that have children (and aren't a source being moved)
              if (!isDisabledToNavigate && hasChildren) {
                toggleFolder(item);
              }
            }}
          >
            <MuiBox
              sx={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <MuiBox sx={{ width: "16px", height: "16px", flexShrink: 0 }}>
                <img src={ArrowIcon} alt="" className="img-full" />
              </MuiBox>

              <MuiBox sx={{ height: "20px", width: "20px", flexShrink: 0 }}>
                <img src={FolderIcon} alt="" className="img-full" />
              </MuiBox>

              <MuiTypography
                variant="14"
                color="text.darkBlack"
                title={item.name}
                sx={{
                  maxWidth: "300px",
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.name}
              </MuiTypography>
            </MuiBox>

            {/* Chevron + count only when folder has children and is navigable */}
            {!isDisabledToNavigate && hasChildren && (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "fit-content",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(item);
                }}
              >
                <MuiBox
                  sx={{
                    px: "8px",
                    bgcolor: "background.gray300",
                    borderRadius: "6px",
                    width: "fit-content",
                  }}
                >
                  <MuiTypography variant="12" color="text.darkBlack">
                    {item?.folder_details?.total_folders ||
                      item?._count?.children}
                  </MuiTypography>
                </MuiBox>

                <MuiBox sx={{ width: "20px", height: "20px" }}>
                  <img src={DropdownArrowIcon} alt="" className="img-full" />
                </MuiBox>
              </MuiBox>
            )}
          </MuiBox>
        </MuiBox>
      );
    });
  };
  const isInitialLoading =
    moveFoldersLoading && !moveFolders?.data?.folders?.length;

  return (
    <MuiBox>
      <MuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "24px",
        }}
      >
        {/* Title – always real */}
        <MuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <MuiTypography fontWeight="600" variant="20" color="text.darkBlack">
            {t("modal.moveTo.moveToFolder")}
          </MuiTypography>
          <MuiBox sx={{ width: "24px", height: "24px" }}>
            <CustomTooltip
              title={t(
                type === "file"
                  ? "modal.moveTo.chooseAFolderToMoveThisFileTo"
                  : "modal.moveTo.chooseAFolderToMoveThisFolderTo",
              )}
              placement="bottom"
            >
              <img src={HelpIcon} alt="helpIcon" />
            </CustomTooltip>
          </MuiBox>
        </MuiBox>
        <SearchInput
          sx={{ height: "40px" }}
          placeholder={t("modal.moveTo.searchFolder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          endAdornment={
            search ? (
              <MuiBox
                component="button"
                type="button"
                aria-label="Clear search"
                onClick={() => setSearch("")}
                sx={{
                  p: 0,
                  m: 0,
                  border: 0,
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <img src={closeIcon} alt="" width="16" height="16" />
              </MuiBox>
            ) : null
          }
        />

        {/* Center – skeleton or content */}
        {isInitialLoading ? (
          <MoveFileFolderSkeleton />
        ) : (
          <>
            <MuiBox>
              {folderHierarchy?.length > 0 && (
                <MuiBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    mb: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <MuiTypography
                    variant="14"
                    color="text.darkBlack"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setExpandedFolders(undefined);
                      setFolderHierarchy([]);
                      setSelectedFolderId(undefined);
                      dispatch(setNullChildFolders());
                    }}
                  >
                    {t(t("common.myDrive"))}
                  </MuiTypography>

                  {folderHierarchy.map((folder, index) => {
                    const displayName =
                      folder.name.length > 20
                        ? `${folder.name.slice(0, 20)}...`
                        : folder.name;

                    return (
                      <React.Fragment key={folder.id}>
                        <MuiBox sx={{ width: "16px", height: "16px" }}>
                          <img src={ArrowRight} alt="" className="img-full" />
                        </MuiBox>

                        <MuiTypography
                          variant="14"
                          color="text.darkBlack"
                          title={folder.name}
                          sx={{
                            cursor:
                              index === folderHierarchy.length - 1
                                ? "default"
                                : "pointer",
                          }}
                          onClick={() => {
                            if (index === folderHierarchy.length - 1) return;

                            const selectedPath = folderHierarchy.slice(
                              0,
                              index + 1,
                            );

                            setFolderHierarchy(selectedPath);
                            setExpandedFolders(
                              selectedPath[selectedPath.length - 1],
                            );
                            setSelectedFolderId(undefined);

                            dispatch(GetChildFolderAction({ id: folder.id }));
                          }}
                        >
                          {displayName}
                        </MuiTypography>
                      </React.Fragment>
                    );
                  })}
                </MuiBox>
              )}

              {(() => {
                const foldersList =
                  childFolders || moveFolders?.data?.folders || [];
                const hasChildrenToShow = foldersList.length > 0;

                // Only show the current/source folder row when there are children
                if (!hasChildrenToShow) return null;

                return (
                  <MuiBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      p: "8px",
                      bgcolor:
                        selectedFolderId === null && !expandedFolders
                          ? "background.blue200"
                          : selectedFolderId === expandedFolders?.id &&
                              expandedFolders
                            ? "background.blue200"
                            : "transparent",
                      borderRadius: "6px",
                    }}
                    onClick={() =>
                      handleSelectFolder(expandedFolders ?? { id: null })
                    }
                  >
                    <MuiBox
                      sx={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      <MuiBox sx={{ height: "20px", width: "20px" }}>
                        <img src={FolderIcon} alt="" className="img-full" />
                      </MuiBox>

                      <MuiTypography
                        variant="14"
                        color="text.darkBlack"
                        title={expandedFolders?.name || t("common.myDrive")}
                        sx={{
                          maxWidth: "300px",
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {expandedFolders?.name || t("common.myDrive")}
                      </MuiTypography>
                    </MuiBox>
                  </MuiBox>
                );
              })()}
              {!moveFoldersLoading &&
                (childFolders || moveFolders?.data?.folders || []).length ===
                  0 && (
                  <NoDataFound
                    title={
                      debouncedSearch.trim()
                        ? "common.nothingToSeeHere"
                        : "searchPage.lookingForSomething"
                    }
                    subTitle={
                      debouncedSearch.trim()
                        ? "searchPage.noSearchFoundMessage"
                        : "searchPage.searchForFilesOrFoldersDesc"
                    }
                  />
                )}

              <MuiBox
                onScroll={handleScroll}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
                className={"no-scrollbar"}
              >
                {renderFolders(childFolders || moveFolders?.data?.folders)}

                {moveFoldersLoading && <MoveFileFolderSkeleton />}
              </MuiBox>
            </MuiBox>
          </>
        )}
      </MuiBox>

      {/* Footer – always real */}
      <MuiBox
        sx={{
          display: "flex",
          gap: "12px",
          justifyContent: "end",
          backgroundColor: "background.gray200",
          p: "16px 24px",
        }}
      >
        <Button
          variant="whiteContained"
          onClick={() => dispatch(closeCenterModal())}
        >
          {t("commonButton.cancel")}
        </Button>
        <SubmitLoaderButton
          variant="blueContained"
          btnText={t("common.move")}
          onClick={() => {
            handleMoveFolder();
          }}
          isLoading={moveFileLoading || movingFolderLoading}
          disabled={isMoveDisabled || moveFileLoading || movingFolderLoading}
        />
      </MuiBox>
    </MuiBox>
  );
};

export default MoveFileFolderModal;
